import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  EnvelopeIcon,
  Cog6ToothIcon,
  InboxIcon,
} from '@heroicons/react/24/outline';
import {
  emailAccountApi,
  emailInboxApi,
  emailSendApi,
  emailFolderApi,
  emailActionsApi,
  emailDraftsApi,
  emailSyncApi,
  EmailFolder,
} from '../../api/emailApi';
import {
  EmailDetail,
  EmailAccountSelector,
  EmailAccountManager,
  EmailSidebar,
  EmailMobileHeader,
  EmailList,
  DraftCompose,
  STARRED_FOLDER,
} from '../email';

const Email = () => {
  // Account state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [showAccountManager, setShowAccountManager] = useState(false);

  // Folder state
  const [currentFolder, setCurrentFolder] = useState(EmailFolder.INBOX);
  const [folderCounts, setFolderCounts] = useState(null);

  // Inbox state
  const [emails, setEmails] = useState([]);
  const [inboxLoading, setInboxLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Email detail state
  const [selectedEmailUid, setSelectedEmailUid] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  // Compose state
  const [showCompose, setShowCompose] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [editingDraftId, setEditingDraftId] = useState(null);

  // Sync state
  const [syncing, setSyncing] = useState(false);

  // Mobile view state
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'detail'

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const data = await emailAccountApi.getAll();
      setAccounts(data || []);
      if (data && data.length > 0 && !selectedAccount) {
        setSelectedAccount(data.find((a) => a.isActive) || data[0]);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to load email accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, [selectedAccount]);

  // Fetch inbox
  const fetchInbox = useCallback(async () => {
    if (!selectedAccount) return;

    setInboxLoading(true);
    try {
      let data;

      // Handle different folder types
      if (currentFolder === STARRED_FOLDER) {
        // Fetch starred emails
        const starredEmails = await emailFolderApi.getStarred(selectedAccount.id);
        data = {
          emails: starredEmails || [],
          totalPages: 1,
          totalEmails: starredEmails?.length || 0,
        };
      } else if (currentFolder === EmailFolder.DRAFTS) {
        // Fetch drafts
        const draftsData = await emailDraftsApi.getAll(selectedAccount.id, page, 20);
        data = {
          emails: (draftsData.drafts || []).map(d => ({
            uid: d.id, // Use draft ID as uid
            isDraft: true,
            from: 'Draft',
            fromEmail: selectedAccount.emailAddress,
            subject: d.subject || '(No Subject)',
            preview: d.preview || '',
            date: d.updatedAt,
            read: true,
            hasAttachments: false,
          })),
          totalPages: draftsData.totalPages || 0,
          totalEmails: draftsData.totalDrafts || 0,
        };
      } else {
        // Use cached metadata for other folders
        data = await emailFolderApi.getByFolder(selectedAccount.id, currentFolder, page, 20);
        // Transform metadata to email format
        if (data.emails) {
          data.emails = data.emails.map(m => ({
            uid: m.messageUid,
            from: m.fromName || m.fromAddress,
            fromEmail: m.fromAddress,
            subject: m.subject,
            preview: m.preview,
            date: m.receivedAt,
            read: m.isRead,
            starred: m.isStarred,
            hasAttachments: m.hasAttachments,
            attachmentCount: m.attachmentCount,
          }));
        }
      }

      setEmails(data.emails || []);
      setTotalPages(data.totalPages || 0);
      setTotalEmails(data.totalEmails || 0);
    } catch (error) {
      console.error('Error fetching inbox:', error);
      // Fallback to direct IMAP fetch if cached data not available
      try {
        const folderMap = {
          [EmailFolder.INBOX]: 'INBOX',
          [EmailFolder.SENT]: 'Sent',
          [EmailFolder.JUNK]: 'Junk',
          [EmailFolder.TRASH]: 'Trash',
          [EmailFolder.ARCHIVE]: 'Archive',
        };
        const data = await emailInboxApi.getInbox(selectedAccount.id, page, 20, folderMap[currentFolder] || 'INBOX');
        setEmails(data.emails || []);
        setTotalPages(data.totalPages || 0);
        setTotalEmails(data.totalEmails || 0);
      } catch (fallbackError) {
        toast.error('Failed to load emails');
        setEmails([]);
      }
    } finally {
      setInboxLoading(false);
    }
  }, [selectedAccount, page, currentFolder]);

  // Fetch email detail
  const fetchEmailDetail = useCallback(async (uid) => {
    if (!selectedAccount || !uid) return;

    // Handle draft selection
    if (currentFolder === EmailFolder.DRAFTS) {
      setEditingDraftId(uid);
      setShowCompose(true);
      return;
    }

    setEmailLoading(true);
    try {
      const folderMap = {
        [EmailFolder.INBOX]: 'INBOX',
        [EmailFolder.SENT]: 'Sent',
        [EmailFolder.JUNK]: 'Junk',
        [EmailFolder.TRASH]: 'Trash',
        [EmailFolder.ARCHIVE]: 'Archive',
      };
      const data = await emailInboxApi.getEmail(selectedAccount.id, uid, folderMap[currentFolder] || 'INBOX');
      setSelectedEmail(data);
      setMobileView('detail');

      // Mark the email as read in the list state
      setEmails(prevEmails =>
        prevEmails.map(email =>
          email.uid === uid ? { ...email, read: true } : email
        )
      );

      // Update read status on server
      emailActionsApi.markRead(selectedAccount.id, uid, true).catch(console.error);
    } catch (error) {
      console.error('Error fetching email:', error);
      toast.error('Failed to load email');
    } finally {
      setEmailLoading(false);
    }
  }, [selectedAccount, currentFolder]);

  // Fetch folder counts
  const fetchFolderCounts = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      const counts = await emailFolderApi.getCounts(selectedAccount.id);
      setFolderCounts(counts);
    } catch (error) {
      console.error('Error fetching folder counts:', error);
    }
  }, [selectedAccount]);

  // Sync emails
  const handleSync = async () => {
    if (!selectedAccount) return;

    setSyncing(true);
    try {
      await emailSyncApi.syncAccount(selectedAccount.id);
      toast.success('Emails synced successfully');
      fetchInbox();
      fetchFolderCounts();
    } catch (error) {
      console.error('Error syncing emails:', error);
      toast.error('Failed to sync emails');
    } finally {
      setSyncing(false);
    }
  };

  // Handle folder change
  const handleFolderChange = (folder) => {
    setCurrentFolder(folder);
    setPage(1);
    setSelectedEmailUid(null);
    setSelectedEmail(null);
  };

  // Initial load
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Load inbox and folder counts when account changes
  useEffect(() => {
    if (selectedAccount) {
      setPage(1);
      setCurrentFolder(EmailFolder.INBOX);
      setSelectedEmailUid(null);
      setSelectedEmail(null);
      fetchInbox();
      fetchFolderCounts();
    }
  }, [selectedAccount]);

  // Reload inbox when page or folder changes
  useEffect(() => {
    if (selectedAccount) {
      fetchInbox();
    }
  }, [page, currentFolder]);

  // Load email detail when selection changes
  useEffect(() => {
    if (selectedEmailUid) {
      fetchEmailDetail(selectedEmailUid);
    }
  }, [selectedEmailUid]);

  // Handle account change
  const handleAccountChange = (account) => {
    setSelectedAccount(account);
  };

  // Handle email selection
  const handleSelectEmail = (uid) => {
    setSelectedEmailUid(uid);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchInbox();
    fetchFolderCounts();
    if (selectedEmailUid) {
      fetchEmailDetail(selectedEmailUid);
    }
  };

  // Handle compose
  const handleCompose = () => {
    setReplyTo(null);
    setEditingDraftId(null);
    setShowCompose(true);
  };

  // Handle reply
  const handleReply = (email) => {
    setReplyTo(email);
    setEditingDraftId(null);
    setShowCompose(true);
  };

  // Handle send email
  const handleSendEmail = async (emailData) => {
    if (!selectedAccount) {
      toast.error('Please select an account first');
      return;
    }

    setSending(true);
    try {
      await emailSendApi.send(selectedAccount.id, emailData, emailData.attachments || []);
      toast.success('Email sent successfully');
      setShowCompose(false);
      setReplyTo(null);
      setEditingDraftId(null);
      // Refresh folder counts
      fetchFolderCounts();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Handle draft saved
  const handleDraftSaved = () => {
    fetchFolderCounts();
    if (currentFolder === EmailFolder.DRAFTS) {
      fetchInbox();
    }
  };

  // Handle draft deleted
  const handleDraftDeleted = () => {
    fetchFolderCounts();
    if (currentFolder === EmailFolder.DRAFTS) {
      fetchInbox();
    }
  };

  // Handle toggle star
  const handleToggleStar = async (uid, starred) => {
    if (!selectedAccount) return;

    try {
      await emailActionsApi.toggleStar(selectedAccount.id, uid, starred);
      setEmails(prevEmails =>
        prevEmails.map(email =>
          email.uid === uid ? { ...email, starred } : email
        )
      );
      fetchFolderCounts();
    } catch (error) {
      console.error('Error toggling star:', error);
      toast.error('Failed to update star');
    }
  };

  // Handle delete email
  const handleDeleteEmail = async (uid, permanent = false) => {
    if (!selectedAccount) return;

    try {
      await emailActionsApi.delete(selectedAccount.id, uid, permanent);
      setEmails(prevEmails => prevEmails.filter(email => email.uid !== uid));
      if (selectedEmailUid === uid) {
        setSelectedEmailUid(null);
        setSelectedEmail(null);
      }
      toast.success(permanent ? 'Email deleted permanently' : 'Email moved to trash');
      fetchFolderCounts();
    } catch (error) {
      console.error('Error deleting email:', error);
      toast.error('Failed to delete email');
    }
  };

  // Handle archive email
  const handleArchiveEmail = async (uid) => {
    if (!selectedAccount) return;

    try {
      await emailActionsApi.archive(selectedAccount.id, uid);
      setEmails(prevEmails => prevEmails.filter(email => email.uid !== uid));
      if (selectedEmailUid === uid) {
        setSelectedEmailUid(null);
        setSelectedEmail(null);
      }
      toast.success('Email archived');
      fetchFolderCounts();
    } catch (error) {
      console.error('Error archiving email:', error);
      toast.error('Failed to archive email');
    }
  };

  // Handle mark as junk
  const handleMarkAsJunk = async (uid) => {
    if (!selectedAccount) return;

    try {
      await emailActionsApi.markAsJunk(selectedAccount.id, uid);
      setEmails(prevEmails => prevEmails.filter(email => email.uid !== uid));
      if (selectedEmailUid === uid) {
        setSelectedEmailUid(null);
        setSelectedEmail(null);
      }
      toast.success('Email marked as junk');
      fetchFolderCounts();
    } catch (error) {
      console.error('Error marking as junk:', error);
      toast.error('Failed to mark as junk');
    }
  };

  // Handle download attachment
  const handleDownloadAttachment = async (attachment) => {
    if (!selectedAccount || !selectedEmailUid) return;

    try {
      const response = await emailInboxApi.downloadAttachment(
        selectedAccount.id,
        selectedEmailUid,
        attachment.index
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Handle back from detail view (mobile)
  const handleBack = () => {
    setMobileView('list');
    setSelectedEmailUid(null);
    setSelectedEmail(null);
  };

  // Account management handlers
  const handleCreateAccount = async (account) => {
    await emailAccountApi.create(account);
    toast.success('Account created successfully');
    fetchAccounts();
  };

  const handleUpdateAccount = async (id, account) => {
    await emailAccountApi.update(id, account);
    toast.success('Account updated successfully');
    fetchAccounts();
  };

  const handleDeleteAccount = async (id) => {
    await emailAccountApi.delete(id);
    toast.success('Account deleted successfully');
    if (selectedAccount?.id === id) {
      setSelectedAccount(null);
    }
    fetchAccounts();
  };

  const handleTestConnection = async (account) => {
    return await emailAccountApi.testConnection(account);
  };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-6 w-6 text-blue-600" />
              <h1 className="text-xl font-semibold text-gray-900">Email</h1>
            </div>
            <EmailAccountSelector
              accounts={accounts}
              selectedAccount={selectedAccount}
              onSelect={handleAccountChange}
              loading={accountsLoading}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAccountManager(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Manage Accounts"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {!selectedAccount ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <EnvelopeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-medium text-gray-900 mb-2">No email account selected</h2>
            <p className="text-gray-500 mb-4">
              {accounts.length === 0
                ? 'Add an email account to get started'
                : 'Select an account from the dropdown'}
            </p>
            {accounts.length === 0 && (
              <button
                onClick={() => setShowAccountManager(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Email Account
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
          {/* Desktop Sidebar with folders (hidden on mobile) */}
          <EmailSidebar
            selectedFolder={currentFolder}
            onSelectFolder={handleFolderChange}
            folderCounts={folderCounts}
            onCompose={handleCompose}
            onSync={handleSync}
            syncing={syncing}
          />

          {/* Email List Column - includes mobile header on small screens */}
          <div
            className={`w-full lg:w-96 xl:w-[400px] flex-shrink-0 lg:border-r border-gray-200 bg-white flex flex-col ${
              mobileView === 'detail' ? 'hidden lg:flex' : 'flex'
            }`}
          >
            {/* Mobile Header - Compose button and horizontal folder tabs */}
            <EmailMobileHeader
              selectedFolder={currentFolder}
              onSelectFolder={handleFolderChange}
              folderCounts={folderCounts}
              onCompose={handleCompose}
              onSync={handleSync}
              syncing={syncing}
            />

            {/* Email List */}
            <div className="flex-1 flex flex-col overflow-hidden">
              <EmailList
                emails={emails}
                loading={inboxLoading}
                selectedEmailUid={selectedEmailUid}
                onSelectEmail={handleSelectEmail}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                page={page}
                totalPages={totalPages}
                totalEmails={totalEmails}
                onPageChange={setPage}
                onRefresh={handleRefresh}
                currentFolder={currentFolder}
                onToggleStar={handleToggleStar}
                onDelete={handleDeleteEmail}
                onArchive={handleArchiveEmail}
                onMarkAsJunk={handleMarkAsJunk}
              />
            </div>
          </div>

          {/* Email Detail */}
          <div
            className={`flex-1 bg-white ${
              mobileView === 'list' ? 'hidden lg:block' : 'block'
            }`}
          >
            <EmailDetail
              email={selectedEmail}
              loading={emailLoading}
              onBack={handleBack}
              onReply={handleReply}
              onDownloadAttachment={handleDownloadAttachment}
            />
          </div>
        </div>
      )}

      {/* Compose Modal with Draft Support */}
      <DraftCompose
        show={showCompose}
        onClose={() => {
          setShowCompose(false);
          setReplyTo(null);
          setEditingDraftId(null);
        }}
        onSend={handleSendEmail}
        replyTo={replyTo}
        sending={sending}
        selectedAccount={selectedAccount}
        draftId={editingDraftId}
        onDraftSaved={handleDraftSaved}
        onDraftDeleted={handleDraftDeleted}
      />

      {/* Account Manager Modal */}
      <EmailAccountManager
        show={showAccountManager}
        accounts={accounts}
        onClose={() => setShowAccountManager(false)}
        onCreate={handleCreateAccount}
        onUpdate={handleUpdateAccount}
        onDelete={handleDeleteAccount}
        onTestConnection={handleTestConnection}
      />
    </div>
  );
};

export default Email;

